import { QueryKeys } from "@app/constants/query-keys.ts";
import { useApis } from "@app/data/apis.ts";
import { InsertWorkLocationPostModel, UpdateAssignmentWorkLocationActivationPostModel } from "@app/data/engine.ts";
import { handleApiError } from "@app/helpers/error-helper.tsx";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useQuery } from "bi-lib";
import { toast } from "react-toastify";

interface Props {
    assignmentIds?: string;
    locationId?: number;
}

export default function useWorkLocationData({ assignmentIds, locationId }: Props = {}) {
    const apis = useApis();
    const queryClient = useQueryClient();

    const getWorkLocationsListQuery = useQuery({
        queryKey: [QueryKeys.WorkLocationList, { assignmentIds }],
        queryFn: () =>
            apis.engine.workLocation.workLocationGetWorkLocationsListList({
                ids: assignmentIds,
                activeOnly: true,
            }),
    });

    const getWorkLocationQuery = useQuery({
        queryKey: [QueryKeys.WorkLocation, { locationId }],
        queryFn: () => apis.engine.workLocation.workLocationGetWorkLocationList({ id: locationId }),
        enabled: locationId != undefined,
    });

    const updateWorkLocationActivationMutation = useMutation({
        mutationFn: (post: UpdateAssignmentWorkLocationActivationPostModel) =>
            apis.engine.workLocation.workLocationUpdateAssignmentWorkLocationActivationCreate(post),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [QueryKeys.WorkLocationList] });
            toast.success("Work location updated");
        },
        onError: (error: Error) => handleApiError(error, "Failed to update work location"),
    });

    const createWorkLocationMutation = useMutation({
        mutationFn: (post: InsertWorkLocationPostModel) =>
            apis.engine.workLocation.workLocationCreateAssignmentWorkLocationCreate(post),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [QueryKeys.WorkLocationList] });
            toast.success("Work location created");
        },
        onError: (error: Error) => handleApiError(error, "Failed to create work location"),
    });

    return {
        workLocationsList: getWorkLocationsListQuery,
        workLocation: getWorkLocationQuery,

        updateWorkLocationActivation: updateWorkLocationActivationMutation,
        createWorkLocation: createWorkLocationMutation,
    };
}
